import { useEffect } from 'react';

import { useApi } from '@backstage/core-plugin-api';

import { trackingApiRef } from '../apis';
import { beginDispatch } from './currentDispatch';
import { createInteractionQueue } from './interactionQueue';
import {
  isTextEntry,
  nativelyActivatesOnEnter,
  resolveHost,
} from './resolveHost';
import { ActivationKind, resolveInteraction } from './resolveInteraction';

const KEYBOARD_ACTIVATION_KEYS = ['Enter', ' ', 'Spacebar'];

export const useInferredTracking = ({
  enabled,
}: {
  enabled: boolean;
}): void => {
  const tracking = useApi(trackingApiRef);

  useEffect(() => {
    if (!enabled || process.env.NODE_ENV === 'test') {
      return undefined;
    }

    const queue = createInteractionQueue(interaction =>
      tracking.trackInteraction(interaction),
    );

    const capture = (event: Event, activation: ActivationKind) => {
      const pageId = document.body.dataset.trackDataPageId;
      if (!pageId) {
        return;
      }
      const interaction = resolveInteraction(event, activation, pageId);
      if (interaction) {
        queue.enqueue(interaction, event);
      }
    };

    const guard = (handler: () => void) => {
      try {
        handler();
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('[InferredTracking] capture failed', error);
        }
      }
    };

    const onClick = (event: Event) =>
      guard(() => {
        if (!event.isTrusted) {
          return;
        }
        beginDispatch(event);
        capture(event, 'pointer');
      });

    const onKeyDown = (event: Event) =>
      guard(() => {
        const key = event as KeyboardEvent;
        if (!event.isTrusted || key.repeat) {
          return;
        }
        if (!KEYBOARD_ACTIVATION_KEYS.includes(key.key)) {
          return;
        }
        beginDispatch(event);
        const host = resolveHost(event);
        if (!host || nativelyActivatesOnEnter(host) || isTextEntry(host)) {
          return;
        }
        capture(event, 'keyboard');
      });

    const onPageHide = () => guard(() => queue.flush());

    const onVisibilityChange = () =>
      guard(() => {
        if (document.visibilityState === 'hidden') {
          queue.flush();
        }
      });

    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      queue.dispose();
    };
  }, [enabled, tracking]);
};
